import type { Content, ContentHref } from '@generalTypes/apiTypes';
import {
  selectApiWithPendingChanges,
  selectChildren,
} from '@newStore/documentApi/documentApiSelectors';
import { createTypedSelector, parentChildRelationFilter } from '@newStore/genericHelpers';
import { referenceFrames } from '@newStore/referenceFrames/referenceFrames';
import { selectKrcReferenceFrameRelationsMap } from '@newStore/referenceFrames/referenceFramesSelectors';
import { RequiredType } from '@nodeTypeConfig/configTypes';
import { createError } from '../createError';
import type { ValidationResult } from '../validationTypes';

const orderError = createError(
  'krcGoalSetItemOrderInvalid',
  'selectIsGoalSetItemOrderValid',
  'Dit <strong>doelenset item</strong> staat niet in de juiste volgorde volgens het ordeningskader.',
  undefined,
  RequiredType.WARNING,
);

export const selectReferenceFrameGoalSetsPositionMap = createTypedSelector(
  [(state) => selectKrcReferenceFrameRelationsMap(state)],
  (relationsMap): Map<ContentHref, number> => {
    const positionMap = new Map<ContentHref, number>();
    if (!relationsMap) {
      return positionMap;
    }

    const relationsTo = relationsMap.to[referenceFrames.krc];
    if (!relationsTo) {
      return positionMap;
    }

    relationsTo
      .filter(parentChildRelationFilter)
      .sort((a, b) => (a.readorder || 0) - (b.readorder || 0))
      .forEach((childRel, index) => {
        positionMap.set(childRel.from.href, index);
      });

    return positionMap;
  },
);

const emptyArray: Content[] = [];

/**
 * Validates that this KRC_GOAL_SET_ITEM is in the correct position relative to its siblings,
 * according to the order of KRC_GOAL_SET nodes in the KRC_REFERENCE_FRAME.
 */
export const selectIsGoalSetItemOrderValid = createTypedSelector(
  [
    (state, href: ContentHref) => selectApiWithPendingChanges(state).content[href],
    (state, href: ContentHref, parentHref: ContentHref | undefined) =>
      parentHref ? selectChildren(state, parentHref) : emptyArray,
    (state) => selectReferenceFrameGoalSetsPositionMap(state),
    (state, href: ContentHref) => href,
  ],
  (
    thisContent: Content | undefined,
    goalSetItemSiblings: Content[],
    goalSetsPositionMap: Map<ContentHref, number>,
    href: ContentHref,
  ): ValidationResult => {
    if (
      !thisContent?.themes?.length ||
      goalSetItemSiblings.length <= 1 ||
      !goalSetsPositionMap.size
    ) {
      return true;
    }

    const thisThemeHref = thisContent.themes[0];

    const thisPosition = goalSetsPositionMap.get(thisThemeHref);
    if (thisPosition === undefined) {
      return true;
    }

    // Find this item's index among siblings
    const thisIndex = goalSetItemSiblings.findIndex((sibling) => sibling.$$meta.permalink === href);

    // Check the previous sibling's theme position
    if (thisIndex > 0) {
      const prevSibling = goalSetItemSiblings[thisIndex - 1];
      const prevThemeHref = prevSibling.themes?.[0];
      const prevPosition = prevThemeHref ? goalSetsPositionMap.get(prevThemeHref) : undefined;
      if (prevPosition !== undefined && prevPosition >= thisPosition) {
        return orderError;
      }
    }

    // Check the next sibling's theme position
    if (thisIndex < goalSetItemSiblings.length - 1) {
      const nextSibling = goalSetItemSiblings[thisIndex + 1];
      const nextThemeHref = nextSibling.themes?.[0];
      const nextPosition = nextThemeHref ? goalSetsPositionMap.get(nextThemeHref) : undefined;
      if (nextPosition !== undefined && nextPosition <= thisPosition) {
        return orderError;
      }
    }

    return true;
  },
);
